// src/api/equipements.api.ts — SCE Issues
// Lookup PN / OF / N° de série / CVE dans SharePoint SCE_Equipements
//
// ⚠️ Mapping colonnes SharePoint (noms internes) :
//   Title                            → NumeroSerie (clé)
//   field_1                          → OF
//   field_2                          → PN
//   Fourniture                       → Fourniture (input Windchill, libre)
//   field_3                          → FamilleEquipement (44 valeurs Sartorius)
//   field_4                          → Designation
//   CVE                              → CVE (numéro de commande)
//   Chef_x0020_de_x0020_projet       → Chef de projet (nom interne SP — les
//                                      espaces sont encodés _x0020_)
//
// ─── Stratégie : cache mémoire + filtrage côté client ───
//
// SharePoint Graph API ne supporte pas le filtre OData sur la colonne `Title`
// (limitation Microsoft documentée — Title est indexé séparément). Plutôt que
// de multiplier les requêtes OData fragiles, on charge TOUTE la liste une fois
// et on filtre en mémoire. À 500 équipements, c'est ~50 Ko de JSON et bien
// plus rapide qu'un round-trip Graph par caractère tapé.
//
// Cache invalidé : à chaque rechargement de page (acceptable — les ajouts
// d'équipements depuis Windchill sont peu fréquents).

import { graphGet } from './graphClient'
import {
  CATEGORIES_PAR_SUFFIXE,
  cleRapprochement,
  suffixeNumeroSerie,
  type Equipement,
  type FamilleEquipement,
  type CategorieEquipement,
} from '@/types/issue.types'
import { nomComplet } from '@/utils/noms'

// ─── Configuration SharePoint ───

const SITE_ID = import.meta.env.VITE_SHAREPOINT_SITE_ID ?? ''
const LIST_ID = import.meta.env.VITE_LIST_EQUIPEMENTS_ID ?? ''

function listPath(): string {
  return `/sites/${SITE_ID}/lists/${LIST_ID}`
}

// ─── Structure brute Graph API ───

interface EquipementFields {
  Title?: string                            // NumeroSerie (clé)
  field_1?: string                          // OF
  field_2?: string                          // PN
  Fourniture?: string                       // input Windchill
  field_3?: FamilleEquipement               // FamilleEquipement (44 valeurs)
  field_4?: string                          // Designation
  CVE?: string                              // numéro de commande
  Client?: string                           // Customer Name de Windchill (20/10)
  // Clé quotée car contient des underscores codés et n'est pas un identifiant
  // TS valide non quoté. C'est le nom interne SP de la colonne "Chef de projet".
  'Chef_x0020_de_x0020_projet'?: string
}

interface SharePointListItem<T> {
  id: string
  fields: T
}

interface SharePointListResponse<T> {
  value: SharePointListItem<T>[]
  '@odata.nextLink'?: string
}

// ─── Cache mémoire ───
//
// On stocke une Promise plutôt que le résultat directement : si plusieurs
// composants demandent simultanément, on ne déclenche qu'une seule requête.

let cacheAll: Promise<Equipement[]> | null = null

/**
 * Vide le cache. À appeler après un import Windchill (V2) ou pour le debug.
 */
export function clearEquipementsCache(): void {
  cacheAll = null
}

// ─── Helpers ───

/**
 * Déduit la **catégorie technique large** d'un équipement à partir du suffixe
 * du numéro de série (règle métier SCE).
 *
 * Utilisée pour remplir SCE_Problemes.field_9 — distincte de la FamilleEquipement
 * fine (44 valeurs côté référentiel SCE_Equipements).
 *
 * ⚠️ La table suffixe → catégorie vit dans `issue.types.ts`
 *    (`CATEGORIES_PAR_SUFFIXE`) : c'est la source unique, partagée avec
 *    l'import Windchill. Ne pas la redupliquer ici.
 *
 * Retourne null si le suffixe n'est pas reconnu (équipement hors périmètre
 * applicatif, ou donnée Windchill incorrecte).
 */
export function deduireCategorieEquipement(
  numeroSerie: string,
): CategorieEquipement | null {
  return CATEGORIES_PAR_SUFFIXE[suffixeNumeroSerie(numeroSerie)] ?? null
}

// ─── Mapper ───

function mapItemToEquipement(
  item: SharePointListItem<EquipementFields>,
): Equipement {
  const f = item.fields
  const numeroSerie = f.Title ?? ''

  const eq: Equipement = {
    numeroSerie,
    of: f.field_1 ?? '',
    pn: f.field_2 ?? '',
    familleEquipement: f.field_3 ?? '',
  }
  if (f.field_4) eq.designation = f.field_4
  if (f.Fourniture) eq.fourniture = f.Fourniture
  if (f.CVE) eq.cve = f.CVE
  if (f.Client?.trim()) eq.client = f.Client.trim()
  if (f['Chef_x0020_de_x0020_projet']) {
    // Windchill donne l'identifiant (« lucile.goy1 ») : affiché « Lucile Goy »,
    // et les comptes homonymes (goy / goy1) ne font plus qu'une personne.
    // SharePoint garde la valeur brute (l'import compare sur elle).
    eq.chefProjet = nomComplet(f['Chef_x0020_de_x0020_projet'])
  }
  return eq
}

// ─── Chargement de la liste complète (avec pagination Graph) ───

async function fetchAllEquipements(): Promise<Equipement[]> {
  const equipements: Equipement[] = []
  let path: string | null = `${listPath()}/items?$expand=fields&$top=200`

  // Boucle de pagination — Graph renvoie 200 items max par page,
  // l'URL de la page suivante est dans @odata.nextLink quand applicable.
  while (path) {
    const response: SharePointListResponse<EquipementFields> =
      await graphGet<SharePointListResponse<EquipementFields>>(path)
    equipements.push(...response.value.map(mapItemToEquipement))
    path = response['@odata.nextLink'] ?? null
  }

  return equipements
}

/**
 * Récupère la liste complète des équipements, en cachant la promise.
 */
async function getAllEquipements(): Promise<Equipement[]> {
  if (!cacheAll) {
    cacheAll = fetchAllEquipements().catch((err) => {
      // En cas d'échec, on invalide le cache pour permettre une nouvelle tentative.
      cacheAll = null
      throw err
    })
  }
  return cacheAll
}

/**
 * Variante publique de `getAllEquipements()`, pour la page de parcours du
 * référentiel (EquipementListPage) qui a besoin de TOUT afficher, pas
 * seulement de filtrer une saisie d'autocomplétion.
 *
 * Repose sur le même cache mémoire — aucune requête supplémentaire si le
 * référentiel est déjà chargé (formulaire de déclaration, dashboard…).
 */
export async function listAllEquipements(): Promise<Equipement[]> {
  return getAllEquipements()
}

// ─── API publique (filtrage côté client) ───

/**
 * Recherche partielle d'équipements sur les 4 clés (NumeroSerie / OF / PN / CVE).
 * Utilisée pour l'autocomplétion du formulaire (debounce 150ms côté page).
 *
 * Min. 2 caractères pour éviter de saturer le rendu en début de saisie.
 *
 * ─── Pourquoi `includes` et non `startsWith` ───
 *
 * Un opérateur lit « OF-2601-0025 » sur un document et tape les 4 derniers
 * chiffres, pas le préfixe `OF-26…` qui est le même sur toute l'année. En
 * `startsWith`, cette saisie ne trouvait rien alors que l'équipement existe :
 * l'échec le plus visible de l'autocomplétion. Le référentiel est déjà en
 * cache mémoire (~500 lignes), le coût du balayage est négligeable.
 *
 * Le tri remonte malgré tout les correspondances par préfixe en premier :
 * quand quelqu'un tape bien le début d'un identifiant, il attend ce résultat
 * en tête, pas noyé au milieu des correspondances internes.
 */
export async function searchEquipements(query: string): Promise<Equipement[]> {
  const q = query.trim().toLowerCase()
  if (q.length < 2) return []

  const all = await getAllEquipements()

  /** Clés interrogeables d'un équipement, en minuscules. */
  function cles(eq: Equipement): string[] {
    return [eq.numeroSerie, eq.of, eq.pn, eq.cve ?? ''].map((v) =>
      v.toLowerCase(),
    )
  }

  return all
    .filter((eq) => cles(eq).some((c) => c.includes(q)))
    .sort((a, b) => {
      const aPrefixe = cles(a).some((c) => c.startsWith(q))
      const bPrefixe = cles(b).some((c) => c.startsWith(q))
      if (aPrefixe !== bPrefixe) return aPrefixe ? -1 : 1
      return a.numeroSerie.localeCompare(b.numeroSerie, 'fr')
    })
    .slice(0, 20)
}

/**
 * Récupère un équipement par son numéro de série (clé — relation 1-1 stricte).
 */
export async function getEquipementByNumeroSerie(
  numeroSerie: string,
): Promise<Equipement | null> {
  const target = cleRapprochement(numeroSerie)
  if (!target) return null
  const all = await getAllEquipements()
  return all.find((eq) => cleRapprochement(eq.numeroSerie) === target) ?? null
}

/**
 * Récupère les équipements rattachés à un OF (relation documentée 1-1).
 */
export async function getEquipementByOF(of: string): Promise<Equipement[]> {
  const target = cleRapprochement(of)
  if (!target) return []
  const all = await getAllEquipements()
  return all.filter((eq) => cleRapprochement(eq.of) === target)
}

/**
 * Récupère les équipements d'un PN (relation 1-N).
 */
export async function getEquipementByPN(pn: string): Promise<Equipement[]> {
  const target = cleRapprochement(pn)
  if (!target) return []
  const all = await getAllEquipements()
  return all.filter((eq) => cleRapprochement(eq.pn) === target)
}

/**
 * Récupère les équipements d'un CVE (numéro de commande, relation 1-N).
 */
export async function getEquipementByCVE(cve: string): Promise<Equipement[]> {
  const target = cleRapprochement(cve)
  if (!target) return []
  const all = await getAllEquipements()
  return all.filter((eq) => cleRapprochement(eq.cve) === target)
}

// ─── Résolution intelligente ───

export type ResolveResult =
  | { mode: 'unique'; equipement: Equipement }
  | { mode: 'liste'; equipements: Equipement[] }
  | { mode: 'introuvable' }

/**
 * Résout l'équipement à partir d'un identifiant saisi librement
 * (PN, OF, N° de série, ou CVE). Heuristique sur le préfixe :
 *
 *  - Commence par "PN"   → getEquipementByPN  (1-N possible → 'liste')
 *  - Commence par "OF-"  → getEquipementByOF  (1-1 normalement)
 *  - Commence par "CVE"  → getEquipementByCVE (1-N possible → 'liste')
 *  - Sinon               → getEquipementByNumeroSerie (1-1 garanti)
 */
export async function resolveEquipement(
  identifier: string,
): Promise<ResolveResult> {
  const value = identifier.trim()
  if (!value) return { mode: 'introuvable' }

  const upper = value.toUpperCase()

  if (upper.startsWith('PN')) {
    const list = await getEquipementByPN(value)
    if (list.length === 0) return { mode: 'introuvable' }
    if (list.length === 1) {
      const first = list[0]
      if (first) return { mode: 'unique', equipement: first }
    }
    return { mode: 'liste', equipements: list }
  }

  if (upper.startsWith('OF-')) {
    const list = await getEquipementByOF(value)
    if (list.length === 0) return { mode: 'introuvable' }
    if (list.length === 1) {
      const first = list[0]
      if (first) return { mode: 'unique', equipement: first }
    }
    return { mode: 'liste', equipements: list }
  }

  if (upper.startsWith('CVE')) {
    const list = await getEquipementByCVE(value)
    if (list.length === 0) return { mode: 'introuvable' }
    if (list.length === 1) {
      const first = list[0]
      if (first) return { mode: 'unique', equipement: first }
    }
    return { mode: 'liste', equipements: list }
  }

  const eq = await getEquipementByNumeroSerie(value)
  return eq ? { mode: 'unique', equipement: eq } : { mode: 'introuvable' }
}

// ─── Chefs de projet (pour le Dashboard) ───

/**
 * Récupère la liste dédupliquée et triée des chefs de projet présents dans
 * SCE_Equipements. Utilisée pour alimenter le dropdown du filtre Dashboard.
 *
 * Repose sur le cache `getAllEquipements()` → pas de requête supplémentaire
 * si le référentiel est déjà chargé.
 */
export async function getAllChefsProjet(): Promise<string[]> {
  const all = await getAllEquipements()
  const set = new Set<string>()
  for (const eq of all) {
    if (eq.chefProjet && eq.chefProjet.trim() !== '') {
      set.add(eq.chefProjet.trim())
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'fr'))
}
